import { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useBusiness } from './contexts/BusinessContext';
import ScreenTransition from './components/ScreenTransition';
import LogoSpinner from './components/LogoSpinner';

import AuthPage from './pages/AuthPage';
import WelcomePage from './pages/WelcomePage';
import BusinessesPage from './pages/BusinessesPage';
import DashboardLayout from './pages/DashboardLayout';
import HomePage from './pages/HomePage';

import PaymentsPage from './pages/PaymentsPage';
import CustomersPage from './pages/CustomersPage';
import ExpensesPage from './pages/ExpensesPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import BalancesPage from './pages/BalancesPage';
import DatabasePage from './pages/DatabasePage';
import MoneyFlowPage from './pages/MoneyFlowPage';
import MfaEnroll from './components/MfaEnroll';
import MfaChallenge from './components/MfaChallenge';

function App() {
  const { user, loading, mfaStatus, refreshMfaStatus } = useAuth();
  const { selectedBusiness, loading: businessLoading } = useBusiness();
  const [showWelcome, setShowWelcome] = useState(false);
  const wasUnauthRef = useRef(true);

  const isAuthenticated = !!user && mfaStatus === 'verified';

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      wasUnauthRef.current = true;
    }
    if (!loading && isAuthenticated && wasUnauthRef.current) {
      wasUnauthRef.current = false;
      setShowWelcome(true);
    }
  }, [loading, isAuthenticated]);

  const handleWelcomeComplete = useCallback(() => setShowWelcome(false), []);

  if (loading || mfaStatus === 'loading' || (user && mfaStatus === 'verified' && businessLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LogoSpinner size={48} />
      </div>
    );
  }

  if (user && mfaStatus === 'enroll_needed') {
    return <MfaEnroll onEnrolled={refreshMfaStatus} />;
  }

  if (user && mfaStatus === 'challenge_needed') {
    return <MfaChallenge onVerified={refreshMfaStatus} />;
  }

  if (showWelcome) {
    return <WelcomePage onComplete={handleWelcomeComplete} />;
  }

  const screenKey = !isAuthenticated ? 'auth' : !selectedBusiness ? 'businesses' : 'dashboard';

  return (
    <ScreenTransition transitionKey={screenKey}>
      {!isAuthenticated ? (
        <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="*" element={<Navigate to="/auth" replace />} />
        </Routes>
      ) : !selectedBusiness ? (
        <Routes>
          <Route path="/businesses" element={<BusinessesPage />} />
          <Route path="*" element={<Navigate to="/businesses" replace />} />
        </Routes>
      ) : (
        <Routes>
          <Route path="/" element={<DashboardLayout />}>
            <Route index element={<HomePage />} />

            <Route path="payments" element={<PaymentsPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="database" element={<DatabasePage />} />
            <Route path="money-flow" element={<MoneyFlowPage />} />
            <Route path="expenses" element={<ExpensesPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="balances" element={<BalancesPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
          <Route path="/businesses" element={<BusinessesPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )}
    </ScreenTransition>
  );
}

export default App;
